import {FormEvent, useEffect, useState} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {
    Collapse,
    Form,
    Input,
    Nav,
    Navbar,
    NavbarBrand,
    NavbarToggler,
    NavItem,
    NavLink
} from 'reactstrap';
import {LogIn, Menu, TreeDeciduous} from 'lucide-react';
import {useAuthStateManager} from '../../hooks/useAuthStateManager';
import {RoutePaths} from '../../RoutePaths';
import ThemeToggle from './ThemeToggle';
import UserMenu from './UserMenu';

const mainLinks = [
    {to: RoutePaths.home(), label: 'Home'},
    {to: RoutePaths.people.root(), label: 'People'},
    {to: RoutePaths.tree(), label: 'Tree'},
    {to: RoutePaths.gallery(), label: 'Gallery'},
    {to: RoutePaths.surnames.root(), label: 'Surnames'}
];

const AppNavbar = () => {
    const {isAuthenticated} = useAuthStateManager();
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const location = useLocation();
    const navigate = useNavigate();

    const [currentPath, setCurrentPath] = useState(location.pathname);

    useEffect(() => {
        setCurrentPath(location.pathname);
        setIsOpen(false);
    }, [location]);

    const toggle = () => setIsOpen(!isOpen);

    const isActive = (path: string) => {
        if (currentPath === path) {
            return true;
        }

        return path !== RoutePaths.home() && currentPath.startsWith(path + '/');
    };

    const onSearch = (event: FormEvent) => {
        event.preventDefault();
        const q = search.trim();
        navigate(
            q
                ? `${RoutePaths.people.root()}?q=${encodeURIComponent(q)}`
                : RoutePaths.people.root()
        );
        setSearch('');
    };

    return (
        <Navbar expand="md" className="ft-navbar navbar-dark">
            <NavbarBrand tag={Link} to={RoutePaths.home()}>
                <TreeDeciduous size={22} />
                <span>Family tree</span>
            </NavbarBrand>
            <NavbarToggler onClick={toggle} aria-label="Toggle navigation">
                <Menu size={24} />
            </NavbarToggler>
            <Collapse isOpen={isOpen} navbar>
                <Nav className="me-auto" navbar>
                    {mainLinks.map(link => (
                        <NavItem key={link.to} active={isActive(link.to)}>
                            <NavLink
                                tag={Link}
                                to={link.to}
                                className={isActive(link.to) ? 'fw-bold' : ''}
                            >
                                {link.label}
                            </NavLink>
                        </NavItem>
                    ))}
                </Nav>
                <Form
                    className="ft-navbar__search d-flex me-md-3 my-2 my-md-0"
                    role="search"
                    onSubmit={onSearch}
                >
                    <Input
                        bsSize="sm"
                        type="search"
                        placeholder="Find a person…"
                        aria-label="Find a person"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </Form>
                <Nav className="ms-md-0 align-items-md-center" navbar>
                    <NavItem>
                        <ThemeToggle />
                    </NavItem>
                    {isAuthenticated ? (
                        <UserMenu />
                    ) : (
                        <NavItem active={isActive(RoutePaths.accounts.login())}>
                            <NavLink
                                tag={Link}
                                to={RoutePaths.accounts.login()}
                                className="d-flex align-items-center"
                            >
                                <LogIn size={14} className="me-1" />
                                Login
                            </NavLink>
                        </NavItem>
                    )}
                </Nav>
            </Collapse>
        </Navbar>
    );
};

export default AppNavbar;
